<script setup lang="ts">
/**
 * TargetingStats — Scan resolution, signature radius, max targets, sensor strength
 */
import type { TargetingStats as TargetingStatsType } from '../../../shared/types';

const props = defineProps<{
  targeting: TargetingStatsType;
}>();

function fmt(n: number): string {
  if (n >= 10000) return (n / 1000).toFixed(1) + 'K';
  return n.toFixed(0);
}

function primarySensor(): { name: string; value: number } {
  const s = props.targeting.sensorStrength;
  const sensors = [
    { name: 'Gravimetric', value: s.gravimetric },
    { name: 'Ladar', value: s.ladar },
    { name: 'Magnetometric', value: s.magnetometric },
    { name: 'Radar', value: s.radar },
  ];
  return sensors.reduce((a, b) => a.value >= b.value ? a : b);
}
</script>

<template>
  <div class="targeting-stats">
    <div class="tgt-row">
      <span class="tgt-label">Scan Resolution</span>
      <span class="tgt-value">{{ fmt(props.targeting.scanResolution) }} mm</span>
    </div>
    <div class="tgt-row">
      <span class="tgt-label">Signature Radius</span>
      <span class="tgt-value">{{ fmt(props.targeting.signatureRadius) }} m</span>
    </div>
    <div class="tgt-row">
      <span class="tgt-label">Max Targets</span>
      <span class="tgt-value">{{ props.targeting.maxTargets }}</span>
    </div>
    <div class="tgt-row" v-if="primarySensor().value > 0">
      <span class="tgt-label">{{ primarySensor().name }}</span>
      <span class="tgt-value">{{ primarySensor().value.toFixed(1) }}</span>
    </div>
  </div>
</template>

<style scoped>
.targeting-stats {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tgt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.tgt-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.tgt-value {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
</style>
